/**
 * Your own account.
 *
 * Two jobs in one screen, because they are the same form:
 *
 *  - a normal visit, where someone changes their password because they want to;
 *  - a forced visit, where an administrator set the current one and the platform
 *    will do nothing else until it is replaced. In that mode the shell renders this
 *    screen instead of the application, and the API refuses every other call, so
 *    there is no route around it.
 *
 * The distinction is one prop. Everything else - the rule, the strength meter, the
 * confirmation - is identical, because the requirement is identical.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  CheckCircle2,
  CircleUserRound,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  PenLine,
  ShieldCheck,
} from "lucide-react";
import { useMemo, useState } from "react";

import { MediaUpload } from "@/components/media/MediaUpload";
import { Field, PageHeader, Panel, PanelHeader } from "@/components/ui/primitives";
import { useMe } from "@/hooks/useSessionData";
import { useI18n } from "@/i18n/I18nProvider";
import { ApiError, api, clearToken } from "@/lib/api";
import { authClient } from "@/lib/auth";
import { cn, toTitleCase } from "@/lib/format";
import type { AccountState } from "@/lib/types";

/**
 * How much work a password is, roughly.
 *
 * Deliberately crude and length-dominated. A precise entropy estimate would be
 * dishonest about what it knows, and the only advice that reliably helps is "make
 * it longer", so that is what the meter rewards.
 */
function strength(value: string): { score: 0 | 1 | 2 | 3; label: string } {
  if (value.length === 0) return { score: 0, label: "" };
  const variety =
    Number(/[a-z]/.test(value)) +
    Number(/[A-Z]/.test(value)) +
    Number(/[0-9]/.test(value)) +
    Number(/[^A-Za-z0-9]/.test(value));
  if (value.length < 12) return { score: 1, label: "Too short" };
  if (value.length >= 20 || (value.length >= 16 && variety >= 2)) {
    return { score: 3, label: "Strong" };
  }
  if (value.length >= 14 || variety >= 3) return { score: 2, label: "Reasonable" };
  return { score: 1, label: "Weak" };
}

export default function ProfilePage({ forced = false }: { forced?: boolean }) {
  const { data: me } = useMe();
  const { t } = useI18n();

  return (
    <>
      {forced ? null : (
        <PageHeader
          title="Your account"
          subtitle="Your password and second factor. Postings and signing rights are set by an administrator."
        />
      )}

      <div className={cn("grid gap-4", forced ? "" : "lg:grid-cols-2")}>
        <ChangePasswordPanel forced={forced} minLength={me?.password_min_length ?? 12} />
        {forced ? null : <SecondFactorPanel />}
        {forced ? null : <PhotoPanel />}
        {forced ? null : <SignaturePanel />}
      </div>

      {forced ? null : (
        <p className="mt-4 text-[11px] text-muted-foreground">
          Signed in as {me?.user.email}
          {me?.user.job_title ? ` · ${me.user.job_title}` : ""}. {t("nav.users")} and role changes
          are made by the quality unit.
        </p>
      )}
    </>
  );
}

function ChangePasswordPanel({ forced, minLength }: { forced: boolean; minLength: number }) {
  const queryClient = useQueryClient();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const meter = useMemo(() => strength(next), [next]);
  const mismatch = confirm.length > 0 && confirm !== next;
  const ready = current.length > 0 && next.length >= minLength && confirm === next;

  const save = useMutation({
    mutationFn: () =>
      api.post<AccountState>("/v1/me/password", {
        current_password: current,
        new_password: next,
      }),
    onSuccess: async () => {
      // The bearer token still carries the old `mustChangePassword` claim for up
      // to half an hour. Dropping it forces a fresh exchange, so the very next
      // request is made under the new state - without which a person who has just
      // set their password would still be held on this screen.
      clearToken();
      setCurrent("");
      setNext("");
      setConfirm("");
      setError(null);
      setDone(true);
      await queryClient.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (caught) => {
      setDone(false);
      if (caught instanceof ApiError) {
        setError(caught.fieldErrors.new_password ?? caught.message);
      } else {
        setError("The password could not be changed");
      }
    },
  });

  return (
    <Panel>
      <PanelHeader
        title={forced ? "Choose your password" : "Change your password"}
        subtitle={
          forced
            ? "Your current password was set by an administrator, so they know it. Replace it to continue."
            : "You will stay signed in here; every other session is signed out."
        }
        icon={<KeyRound size={15} />}
      />

      <div className="space-y-3 p-4">
        <Field label={forced ? "Temporary password" : "Current password"} required>
          <input
            type={reveal ? "text" : "password"}
            className="field"
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
            autoComplete="current-password"
            autoFocus
          />
        </Field>

        <Field label="New password" required>
          <div className="relative">
            <input
              type={reveal ? "text" : "password"}
              className="field pr-9"
              value={next}
              onChange={(event) => setNext(event.target.value)}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              onClick={() => setReveal((value) => !value)}
              aria-label={reveal ? "Hide passwords" : "Show passwords"}
            >
              {reveal ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
          <StrengthBar meter={meter} />
          <p className="mt-1 text-[11px] text-muted-foreground">
            At least {minLength} characters. Length matters far more than punctuation: four
            unrelated words beat a short password with a symbol in it.
          </p>
        </Field>

        <Field label="Repeat the new password" required error={mismatch ? "These do not match" : undefined}>
          <input
            type={reveal ? "text" : "password"}
            className="field"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            autoComplete="new-password"
          />
        </Field>

        {error ? (
          <p className="flex items-start gap-2 rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            {error}
          </p>
        ) : null}

        {done ? (
          <p className="flex items-start gap-2 rounded-md bg-status-approved-bg px-3 py-2 text-xs text-status-approved">
            <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
            Password changed. Your other sessions have been signed out.
          </p>
        ) : null}

        <button
          className="btn btn-primary w-full"
          disabled={!ready || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? <Loader2 size={15} className="animate-spin" /> : null}
          {toTitleCase(forced ? "Set my password and continue" : "Change password")}
        </button>
      </div>
    </Panel>
  );
}

function StrengthBar({ meter }: { meter: { score: 0 | 1 | 2 | 3; label: string } }) {
  if (meter.score === 0) return null;
  const tone =
    meter.score === 3
      ? "bg-status-approved"
      : meter.score === 2
        ? "bg-status-pending"
        : "bg-destructive";
  return (
    <div className="mt-1.5 flex items-center gap-2">
      <div className="flex h-1 flex-1 gap-1" aria-hidden="true">
        {[1, 2, 3].map((step) => (
          <span
            key={step}
            className={cn("flex-1 rounded-full", step <= meter.score ? tone : "bg-border")}
          />
        ))}
      </div>
      <span className="w-20 text-right text-[11px] text-muted-foreground">{meter.label}</span>
    </div>
  );
}

/**
 * Second factor.
 *
 * Enrolment is left to better-auth's own endpoints: the TOTP secret and its QR
 * code should be produced by the service that will verify them, and passing them
 * through this API would put a live secret in a second place for no benefit.
 */
function SecondFactorPanel() {
  const [password, setPassword] = useState("");
  const [uri, setUri] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enrolled, setEnrolled] = useState(false);
  const [busy, setBusy] = useState(false);

  const begin = async () => {
    setError(null);
    setBusy(true);
    try {
      const { data, error: failed } = await authClient.twoFactor.enable({ password });
      if (failed) throw new Error(failed.message ?? "That password was not accepted");
      setUri(data?.totpURI ?? null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not start enrolment");
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    setError(null);
    setBusy(true);
    try {
      const { error: failed } = await authClient.twoFactor.verifyTotp({ code });
      if (failed) throw new Error(failed.message ?? "That code was not accepted");
      setEnrolled(true);
      setUri(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not confirm the code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel>
      <PanelHeader
        title="Two-factor authentication"
        subtitle="Required in practice for anyone who signs certificates: a password alone is not enough to authorise a release."
        icon={<ShieldCheck size={15} />}
      />
      <div className="space-y-3 p-4">
        {enrolled ? (
          <p className="flex items-start gap-2 rounded-md bg-status-approved-bg px-3 py-2 text-xs text-status-approved">
            <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
            Authenticator enrolled. You will be asked for a code at your next sign-in.
          </p>
        ) : uri ? (
          <>
            <p className="text-xs text-muted-foreground">
              Add this secret to your authenticator app, then enter the six-digit code it shows.
            </p>
            <code className="block break-all rounded-md bg-muted px-3 py-2 text-[11px]">{uri}</code>
            <Field label="Code from your app" required>
              <input
                className="field text-center font-mono text-lg tracking-[0.3em]"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
              />
            </Field>
            <button
              className="btn btn-primary w-full"
              disabled={code.length !== 6 || busy}
              onClick={() => void finish()}
            >
              {busy ? <Loader2 size={15} className="animate-spin" /> : null}
              {toTitleCase("Confirm enrolment")}
            </button>
          </>
        ) : (
          <>
            <Field label="Confirm your password" required>
              <input
                type="password"
                className="field"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
              />
            </Field>
            <button
              className="btn btn-outline w-full"
              disabled={password.length === 0 || busy}
              onClick={() => void begin()}
            >
              {busy ? <Loader2 size={15} className="animate-spin" /> : null}
              {toTitleCase("Set up an authenticator")}
            </button>
          </>
        )}

        {error ? (
          <p className="flex items-start gap-2 rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            {error}
          </p>
        ) : null}

        <p className="text-[11px] text-muted-foreground">
          Lost your device? An administrator can clear the enrolment so you can enrol a new one;
          the secret only ever exists on the device, so there is nothing to recover.
        </p>
      </div>
    </Panel>
  );
}


/**
 * Your photo, as it appears beside your name across the platform.
 *
 * The opposite of the signature in both rules that matter: anyone signed in can
 * see it — that is what it is for — and replacing it disposes of the old image,
 * because nothing cites an avatar the way a certificate cites a signature.
 */
function PhotoPanel() {
  const { data: me } = useMe();
  const queryClient = useQueryClient();

  return (
    <Panel>
      <PanelHeader
        title="Your photo"
        subtitle="Shown beside your name: in the header, the staff register, and workflow history"
        icon={<CircleUserRound size={15} />}
      />
      <div className="p-4">
        <MediaUpload
          title={toTitleCase("Profile photo")}
          hint="A square photo of your face works best. PNG or JPEG, up to 2 MB."
          endpoint="/v1/me/avatar"
          imagePath="/v1/me/avatar"
          present={Boolean(me?.user.avatar_path)}
          canEdit
          canRemove
          previewClassName="size-20 overflow-hidden rounded-full p-0"
          imageClassName="size-full object-cover"
          onChanged={() => {
            void queryClient.invalidateQueries({ queryKey: ["me"] });
            void queryClient.invalidateQueries({ queryKey: ["avatar"] });
          }}
        />
        <p className="mt-3 text-[11px] text-muted-foreground">
          Visible to anyone signed in here. It never appears on certificates or anything a
          client receives.
        </p>
      </div>
    </Panel>
  );
}


/**
 * Your signature, as it appears on certificates you sign.
 *
 * Only you can put it here. An administrator can clear it — a bad scan, or somebody who
 * has left — but there is deliberately no way for them to upload one on your behalf,
 * because that would let whoever holds `admin.users` produce a certificate signed in
 * your name.
 *
 * Replacing it does not reach backwards. Certificates already issued recorded the exact
 * image they were rendered with and keep rendering that way, which is the only behaviour
 * an issued document can have.
 */
function SignaturePanel() {
  const { data: me } = useMe();
  const queryClient = useQueryClient();

  return (
    <Panel>
      <PanelHeader
        title="Your signature"
        subtitle="Drawn above your name on certificates you prepare, verify or authorise"
        icon={<PenLine size={15} />}
      />
      <div className="p-4">
        <MediaUpload
          title={toTitleCase("Signature image")}
          hint="A scan of your signature on white paper. PNG with a transparent background reproduces best over the signature rule; JPEG is fine. Up to 2 MB."
          endpoint="/v1/me/signature"
          imagePath="/v1/me/signature"
          present={Boolean(me?.user.signature_path)}
          canEdit
          canRemove
          onChanged={() => {
            void queryClient.invalidateQueries({ queryKey: ["me"] });
          }}
        />
        <p className="mt-3 text-[11px] text-muted-foreground">
          Uploading a signature does not change what you are authorised to sign. Postings
          and signing rights are set by the quality unit.
        </p>
      </div>
    </Panel>
  );
}
